"use client";

import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Calendar, User } from "lucide-react";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-12">
        <div className="h-12 w-3/4 animate-pulse rounded-sm bg-muted/30" />
        <div className="mt-8 h-96 w-full animate-pulse rounded-sm bg-muted/30" />
        <div className="mt-8 space-y-4">
          <div className="h-4 w-full animate-pulse rounded-sm bg-muted/30" />
          <div className="h-4 w-full animate-pulse rounded-sm bg-muted/30" />
          <div className="h-4 w-2/3 animate-pulse rounded-sm bg-muted/30" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-12">
        <p className="text-muted-foreground">Failed to load post. Please try again later.</p>
      </div>
    );
  }

  const post = posts[0];

  if (!post) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-12">
        <p className="text-muted-foreground">Post not found.</p>
      </div>
    );
  }

  return (
    <article className="mx-auto max-w-4xl px-6 py-12">
      <header className="mb-8">
        <h1 className="font-display text-4xl font-bold leading-tight tracking-tight text-foreground md:text-5xl">
          {post.title}
        </h1>
        
        <div className="mt-6 flex items-center gap-4 text-sm text-muted-foreground">
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="size-4" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric"
                })}
              </time>
            </div>
          )}
          
          {post.author && (
            <>
              <Separator orientation="vertical" className="h-4" />
              <div className="flex items-center gap-2">
                <User className="size-4" />
                <span>{post.author.name}</span>
              </div>
            </>
          )}
        </div>
      </header>

      {post.featuredImage && (
        <div className="mb-8 overflow-hidden rounded-sm">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="h-auto w-full object-cover"
          />
        </div>
      )}

      {post.content && (
        <div
          className="prose prose-lg max-w-none text-foreground [&_a]:text-accent [&_a]:underline [&_a]:decoration-accent/30 [&_a:hover]:decoration-accent [&_h1]:font-display [&_h1]:font-bold [&_h2]:font-display [&_h2]:font-bold [&_h3]:font-display [&_h3]:font-bold [&_h4]:font-display [&_h4]:font-bold [&_h5]:font-display [&_h5]:font-bold [&_h6]:font-display [&_h6]:font-bold"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {post.author && (
        <footer className="mt-12 border-t border-border pt-8">
          <div className="flex items-start gap-4">
            <Avatar className="size-16">
              {post.author.avatar?.["96"] && (
                <AvatarImage src={post.author.avatar["96"]} alt={post.author.name} />
              )}
              <AvatarFallback className="bg-primary text-foreground-primary">
                {post.author.name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1">
              <h3 className="font-display text-lg font-semibold text-foreground">
                {post.author.name}
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Author
              </p>
            </div>
          </div>
        </footer>
      )}
    </article>
  );
}

export default function SinglePostTemplateSection() {
  const currentPostId = wvcClient.getCurrentPostID();

  return (
    <section id="single-post-template-section" className="bg-background">
      <PostsProvider postType="post" wp_query={{ include: [currentPostId], per_page: 1 }} embeds={["wp:featuredmedia", "wp:term", "author"]} fields={["id", "title.rendered", "content.rendered", "excerpt.rendered", "date", "author", "categories", "tags", "link", "_embedded", "featured_media", "meta", "_links.wp:featuredmedia", "_links.wp:term", "_links.author"]}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}